import { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import useSWR from "swr";
import { api } from "@/lib/api";
import { trackEventOnce } from "@/lib/analytics";
import Reveal from "@/components/site/Reveal";
import Seo, { canonicalBase } from "@/components/site/Seo";
import PageHeader from "@/components/site/PageHeader";
import IdeaForm from "@/components/site/IdeaForm";
import ReaderIdeas from "@/components/site/ReaderIdeas";
import { formatArticleDate } from "@/pages/Diario";
import { useSettings } from "@/context/SettingsContext";

const fetcher = (url) => api.get(url).then((r) => r.data);

/** Categorie del Diario con una pagina pubblica dedicata. */
export const CATEGORY_PAGES = {
  "idee-per-il-borgo": {
    name: "Idee per il Borgo",
    subtitle: "Cosa potremmo fare",
    title: "Idee per il Borgo · Proposte per il futuro dei piccoli centri",
    description:
      "Idee e proposte per valorizzare i piccoli centri: attrattività, recupero intelligente degli immobili, ospitalità, cultura, spazio pubblico e mobilità. Esempi documentati e proposte concrete per Aterrana e i borghi ben collegati.",
    lead: "Si parte da quello che c'è già: identità, tessuto storico, prodotti del territorio e una posizione ben collegata. Esempi che funzionano altrove e proposte concrete per valorizzarlo ancora di più.",
  },
};

export default function DiarioCategoria() {
  const categoria = useLocation().pathname.split("/").filter(Boolean).pop();
  const hash = useLocation().hash;
  const meta = CATEGORY_PAGES[categoria];
  const { settings } = useSettings();
  const { data, isLoading } = useSWR(
    meta ? `/diario/articles?limit=24&cluster=${categoria}` : null,
    fetcher,
    { revalidateOnFocus: false },
  );
  const items = useMemo(() => data?.items || [], [data]);
  const path = `/diario-dal-borgo/${categoria}`;

  useEffect(() => {
    if (!meta) return;
    // Evento non personale, distinto dal page_view: nessun secondo tag, nessun doppione.
    trackEventOnce(`categoria:${categoria}`, "diario_category_view", { article_category: categoria });
    const base = canonicalBase();
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "categoria-ld";
    ld.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: meta.name,
        description: meta.description,
        inLanguage: "it-IT",
        url: `${base}${path}`,
        isPartOf: { "@type": "Blog", name: "Diario dal Borgo", url: `${base}/diario-dal-borgo` },
        mainEntity: {
          "@type": "ItemList",
          itemListElement: items.map((a, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${base}/diario-dal-borgo/${a.slug}`,
            name: a.title,
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Diario dal Borgo", item: `${base}/diario-dal-borgo` },
          { "@type": "ListItem", position: 2, name: meta.name, item: `${base}${path}` },
        ],
      },
    ]);
    document.getElementById("categoria-ld")?.remove();
    document.head.appendChild(ld);
    return () => document.getElementById("categoria-ld")?.remove();
  }, [meta, categoria, items, path]);

  useEffect(() => {
    if (hash !== "#modulo-idee") return;
    // La lista sopra il modulo cambia altezza mentre carica: allineiamo due volte.
    const scroll = () => document.getElementById("modulo-idee")?.scrollIntoView({ behavior: "smooth", block: "start" });
    const timers = [setTimeout(scroll, 350), setTimeout(scroll, 1100)];
    return () => timers.forEach(clearTimeout);
  }, [hash, isLoading, items.length]);

  if (!meta) {
    return (
      <div className="pt-40 pb-32 container-ed">
        <h1 className="font-serif text-4xl text-wood">Categoria non trovata</h1>
        <Link to="/diario-dal-borgo" className="mt-8 inline-flex text-sm text-sage link-underline">
          Torna al Diario
        </Link>
      </div>
    );
  }

  return (
    <>
      <Seo title={meta.title} description={meta.description} path={path} />
      <PageHeader
        eyebrow={meta.subtitle}
        title={meta.name}
        lead={meta.lead}
        label="BORGO PHOTO"
        alt="Il borgo di Aterrana"
        src={settings.borgo_header_image}
      />

      <section className="section-y bg-cream">
        <div className="container-ed" data-testid="categoria-page">
          <nav className="pb-8 text-xs tracking-wider text-foreground/50" aria-label="Percorso">
            <Link to="/diario-dal-borgo" className="text-sage link-underline">Diario dal Borgo</Link>
            <span className="px-2">/</span>
            <span>{meta.name}</span>
          </nav>

          {isLoading && <p className="text-sm text-foreground/55">Carichiamo gli articoli…</p>}

          {!isLoading && items.length === 0 && (
            <div className="border border-dashed border-wood/25 px-6 py-14 text-center" data-testid="categoria-empty">
              <p className="font-serif text-2xl text-wood">I primi articoli stanno arrivando</p>
              <p className="mt-3 text-sm text-foreground/65">
                Questa categoria è appena nata: torna a trovarci tra qualche giorno.
              </p>
            </div>
          )}

          {items.length > 0 && (
            <ul className="grid gap-x-12 gap-y-12 md:grid-cols-2 lg:grid-cols-3" data-testid="categoria-list">
              {items.map((a, i) => (
                <Reveal as="li" key={a.id} delay={Math.min(i * 0.05, 0.3)} className="border-t border-wood/15 pt-6">
                  <p className="eyebrow text-gold">{a.category_name}</p>
                  <h2 className="mt-4 font-serif text-2xl leading-snug text-wood">
                    <Link to={`/diario-dal-borgo/${a.slug}`} className="hover:text-sage transition-colors duration-400">
                      {a.title}
                    </Link>
                  </h2>
                  <p className="mt-3 text-sm text-foreground/65 leading-relaxed">{a.excerpt}</p>
                  <p className="mt-4 text-xs tracking-wider text-foreground/45">{formatArticleDate(a.published_at)}</p>
                </Reveal>
              ))}
            </ul>
          )}

          <div className="mt-16 border-t border-wood/15 pt-8 text-sm text-foreground/70">
            <p>
              Queste pagine nascono da quello che vediamo stando qui:{" "}
              <Link to="/il-borgo" className="text-sage underline underline-offset-4">il borgo di Aterrana</Link> e{" "}
              <Link to="/progetti-futuri" className="text-sage underline underline-offset-4">i progetti futuri</Link>{" "}
              raccontano il contesto da cui partiamo.
            </p>
          </div>

          <div className="mt-20 border-t border-wood/15 pt-16 pb-[19rem] lg:pb-20">
            <IdeaForm />
          </div>

          <div className="mt-20">
            <ReaderIdeas />
          </div>
        </div>
      </section>
    </>
  );
}
